import type {
	BotWizardApiField,
	BotWizardApiNameField,
	BotWizardApiPersonalityField,
	BotWizardApiSimpleField,
} from '~services/api/generation/types';

import type {
	BotWizardConfigField,
	BotWizardContentDependencyData,
	BotWizardContentDependencyStep,
	BotWizardField,
	BotWizardFieldButton,
	BotWizardFieldColor,
	BotWizardFieldImage,
	BotWizardFieldNames,
	BotWizardFieldSliders,
	BotWizardFieldText,
	BotWizardFieldType,
	BotWizardValueKeys,
	BotWizardValueMultiselect,
	BotWizardValues,
	BotWizardValueSimple,
	BotWizardValueSliders,
} from './types';

export const isData = <T>(
	data: BotWizardContentDependencyData<T>
): data is T => {
	return (
		Array.isArray(data) ||
		(typeof data === 'object' &&
			data !== null &&
			'isData' in data &&
			data.isData === true)
	);
};

export const isStep = (
	data: BotWizardContentDependencyStep
): data is BotWizardFieldNames[] => {
	return Array.isArray(data);
};

export const isApiFieldSimple = (
	field:
		| BotWizardApiField<BotWizardApiNameField>
		| BotWizardApiPersonalityField
		| BotWizardApiField<BotWizardApiSimpleField>,
	type: BotWizardFieldType
): field is BotWizardApiField<BotWizardApiSimpleField> =>
	type !== 'sliders' && type !== 'text';

export const isApiFieldPersonality = (
	field:
		| BotWizardApiField<BotWizardApiNameField>
		| BotWizardApiPersonalityField
		| BotWizardApiField<BotWizardApiSimpleField>,
	type: BotWizardFieldType
): field is BotWizardApiPersonalityField => type === 'sliders';

export const isApiFieldName = (
	field:
		| BotWizardApiField<BotWizardApiNameField>
		| BotWizardApiPersonalityField
		| BotWizardApiField<BotWizardApiSimpleField>,
	type: BotWizardFieldType
): field is BotWizardApiField<BotWizardApiNameField> => type === 'text';

export const isConfigFieldColor = (
	field: BotWizardConfigField<BotWizardFieldType>
): field is BotWizardConfigField<'color'> => {
	return field.type === 'color';
};

export const isConfigFieldImage = (
	field: BotWizardConfigField<BotWizardFieldType>
): field is BotWizardConfigField<'image'> => {
	return field.type === 'image';
};

export const isConfigFieldButton = (
	field: BotWizardConfigField<BotWizardFieldType>
): field is BotWizardConfigField<'button'> => {
	return field.type === 'button';
};

export const isConfigFieldText = (
	field: BotWizardConfigField<BotWizardFieldType>
): field is BotWizardConfigField<'text'> => {
	return field.type === 'text';
};

export const isConfigFieldSliders = (
	field: BotWizardConfigField<BotWizardFieldType>
): field is BotWizardConfigField<'sliders'> => {
	return field.type === 'sliders';
};

export const isFieldImage = (
	data: BotWizardField
): data is BotWizardFieldImage => {
	return data.type === 'image';
};

export const isFieldColor = (
	data: BotWizardField
): data is BotWizardFieldColor => {
	return data.type === 'color';
};

export const isFieldButton = (
	data: BotWizardField
): data is BotWizardFieldButton => {
	return data.type === 'button';
};

export const isFieldText = (
	data: BotWizardField
): data is BotWizardFieldText => {
	return data.type === 'text';
};

export const isFieldSliders = (
	data: BotWizardField
): data is BotWizardFieldSliders => {
	return data.type === 'sliders';
};

export function isSingleValueSimple(
	field: BotWizardField,
	value:
		| BotWizardValueSimple
		| BotWizardValueMultiselect
		| BotWizardValueSliders
): value is BotWizardValueSimple {
	return (
		isFieldText(field) ||
		((isFieldColor(field) || isFieldButton(field) || isFieldImage(field)) &&
			!field.multiSelect)
	);
}

export function isValueSimple(
	field: BotWizardField,
	values: BotWizardValues,
	key: BotWizardValueKeys
): values is { [K in typeof key]: BotWizardValueSimple } {
	return isSingleValueSimple(field, values[key]);
}

export function isSingleValueMultiSelect(
	field: BotWizardField,
	value:
		| BotWizardValueSimple
		| BotWizardValueMultiselect
		| BotWizardValueSliders
): value is BotWizardValueMultiselect {
	return (
		(isFieldColor(field) || isFieldButton(field) || isFieldImage(field)) &&
		!!field.multiSelect
	);
}

export function isValueMultiSelect(
	field: BotWizardField,
	values: BotWizardValues,

	key: BotWizardValueKeys
): values is { [K in typeof key]: BotWizardValueMultiselect } {
	return isSingleValueMultiSelect(field, values[key]);
}

export function isSingleValueSliders(
	field: BotWizardField,
	value:
		| BotWizardValueSimple
		| BotWizardValueMultiselect
		| BotWizardValueSliders
): value is BotWizardValueSliders {
	return isFieldSliders(field);
}

export function isValueSliders(
	field: BotWizardField,
	values: BotWizardValues,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	key: BotWizardValueKeys
): values is { [K in typeof key]: BotWizardValueSliders } {
	return isFieldSliders(field);
}

export function isBotWizardFieldName(
	key: string,
	fields: Record<BotWizardFieldNames, BotWizardField>
): key is BotWizardFieldNames {
	return key in fields;
}
